import { documentsApiRef } from '@agilelab/plugin-wb-builder-catalog';
import {
  Acceptance_type,
  WitboostDocument,
} from '@agilelab/plugin-wb-documents-common';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useEffect, useState } from 'react';
import { DocumentsAcceptanceActions } from './AcceptanceActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        height: 'calc(100vh - 64px)',
        maxHeight: 'calc(100vh - 64px)',
        margin: '32px auto',
      },
    },
    dialogContent: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
    },
    container: {
      background: theme.palette.background.paper,
      padding: '0px',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignSelf: 'stretch',
    },
    pdfContainer: {
      position: 'relative',
      width: '100%',
      flexGrow: 1,
      minHeight: '500px',
    },
    actionBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
    },
    page: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
);

export function DocumentsPopup(_props: {}) {
  const documentsApi = useApi(documentsApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);
  const theme = useTheme();
  const classes = useStyles();

  const [showDocumentsPopup, setShowDocumentsPopup] = useState(false);
  const [documents, setDocuments] = useState<WitboostDocument[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPdfLoading, setPdfLoading] = useState<boolean>(true);

  const handleAcceptClick = async (): Promise<void> => {
    if (documents[currentDocumentIndex]) {
      const currentDocument = documents[currentDocumentIndex];

      try {
        setLoading(true);
        await documentsApi.acceptDocumentFromUser(currentDocument.id!);

        if (currentDocumentIndex === documents.length - 1) {
          setShowDocumentsPopup(false);
          return;
        }
        setPdfLoading(true);
        setCurrentDocumentIndex(prevIndex => prevIndex + 1);
      } catch (error) {
        alertApi.post({
          message: `Error accepting document ${currentDocument.name}. Contact the platform team for more info.`,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReject = () => {
    identityApi.signOut();
  };

  useEffect(() => {
    const documentsEnabled: boolean =
      configApi.getOptionalBoolean(
        'mesh.documents.requireDocumentAcceptance',
      ) ?? false;
    try {
      if (!documentsEnabled) {
        setDocuments([]);
        setShowDocumentsPopup(false);
        return;
      }
      const fetchData = async () => {
        const fetchedDocuments = await documentsApi.retrieveDocuments({
          not_accepted_only: true,
          enabled_only: true,
          acceptance_types: [
            Acceptance_type.MANDATORY,
            Acceptance_type.ACKNOWLEDGMENT,
          ],
        });

        setDocuments(fetchedDocuments);
        if (fetchedDocuments.length > 0) {
          setShowDocumentsPopup(true);
        }
      };

      fetchData();
    } catch (error) {
      alertApi.post({
        error: error,
        severity: 'error',
      });
      setShowDocumentsPopup(false);
    }
  }, [identityApi, documentsApi, alertApi, configApi]);

  if (!showDocumentsPopup) {
    return null;
  }

  return (
    <Dialog
      open={showDocumentsPopup}
      className={classes.dialog}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle color="primary">
        {documents[currentDocumentIndex]?.name}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText id="alert-dialog-description">
          Read and express your acceptance of the following document.
        </DialogContentText>
        {documents[currentDocumentIndex] && (
          <Box className={classes.container}>
            {(() => {
              const blob = new Blob([documents[currentDocumentIndex].content], {
                type: 'application/pdf',
              });
              // FIXME: revoke the object URL when the component unmounts to prevent memory leaks
              const url = URL.createObjectURL(blob);

              return (
                <div className={classes.pdfContainer}>
                  {isPdfLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <CircularProgress />
                    </div>
                  )}

                  <object
                    data={url}
                    type="application/pdf"
                    style={{
                      border: 'none',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      maxWidth: '100%',
                    }}
                    onLoad={() => setPdfLoading(false)}
                  >
                    <Typography>
                      Your browser doesn't support PDF viewing. Please{' '}
                      <a
                        href={url}
                        download
                        style={{ color: theme.palette.primary.main }}
                        aria-label="Download PDF document"
                        title="Download PDF document"
                      >
                        download the PDF
                      </a>{' '}
                      to view it.
                    </Typography>
                  </object>
                </div>
              );
            })()}
          </Box>
        )}
      </DialogContent>
      <Box className={classes.actionBox}>
        <Typography>
          Document {currentDocumentIndex + 1} of {documents.length}
        </Typography>
        <Box padding={theme.spacing(0.1)} style={{ textAlign: 'center' }}>
          {documents[currentDocumentIndex].name}
        </Box>
        <DialogActions>
          <DocumentsAcceptanceActions
            type={documents[currentDocumentIndex]?.acceptance_type}
            onAcceptAction={handleAcceptClick}
            onDeclineAction={handleReject}
            loading={loading}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
}
